import { Link } from 'react-router-dom';
import { categories as categoriesData } from '@/mocks/articles';

interface ArticleCardProps {
  id: number;
  title: string;
  excerpt: string;
  categoryId: string;
  author: string;
  date: string;
  image: string;
  readTime: number;
  featured?: boolean;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({
  id,
  title,
  excerpt,
  categoryId,
  author,
  date,
  image,
  readTime,
  variant = 'default',
}: ArticleCardProps) {
  const category = categoriesData.find((c) => c.id === categoryId);

  if (variant === 'horizontal') {
    return (
      <article className="group flex flex-col sm:flex-row gap-5 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer">
        <Link to={`/article/${id}`} className="block w-full sm:w-48 flex-shrink-0 rounded-lg overflow-hidden">
          <div className="w-full sm:w-48 h-32">
            <img
              src={image}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              title={`${title} - 蜜桃网`}
            />
          </div>
        </Link>
        <div className="flex flex-col justify-between flex-1 min-w-0">
          <div>
            <div className="flex items-center gap-3 mb-2">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md whitespace-nowrap cursor-pointer"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-xs text-foreground-400">{readTime} 分钟阅读</span>
            </div>
            <Link to={`/article/${id}`}>
              <h3 className="font-heading text-base text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 mb-2 line-clamp-2">
                {title}
              </h3>
            </Link>
            <p className="text-sm text-foreground-500 line-clamp-2">
              {excerpt}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
            <span>{author}</span>
            <span>·</span>
            <span>{date}</span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group flex items-start gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
        <Link to={`/article/${id}`} className="block flex-shrink-0">
          <div className="w-16 h-16 rounded-lg overflow-hidden">
            <img
              src={image}
              alt={title}
              className="w-full h-full object-cover"
              title={`${title} - 蜜桃网`}
            />
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/article/${id}`}>
            <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
              {title}
            </h4>
          </Link>
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            {category && <span className="text-primary-500">{category.name}</span>}
            <span>·</span>
            <span>{date}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col">
      <Link to={`/article/${id}`} className="block">
        <div className="w-full h-48 overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            title={`${title} - 蜜桃网`}
          />
        </div>
      </Link>
      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-center gap-3 mb-3">
          {category && (
            <Link
              to={`/category/${category.slug}`}
              className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md whitespace-nowrap cursor-pointer"
            >
              {category.name}
            </Link>
          )}
          <span className="text-xs text-foreground-400">{readTime} 分钟阅读</span>
        </div>
        <Link to={`/article/${id}`} className="flex-1">
          <h3 className="font-heading text-lg text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 mb-2 line-clamp-2">
            {title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-4">
          {excerpt}
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-background-100">
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <span>{author}</span>
            <span>·</span>
            <span>{date}</span>
          </div>
          <Link
            to={`/article/${id}`}
            className="text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
          >
            阅读全文
            <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}